import { JSX } from "react";
import { BaseComponent } from "../../Core/BaseComponent/BaseComponent";
import { SerendieSymbol, SerendieSymbolMoreVertical } from "@serendie/symbols";
import "./Styles.scss";
import { IconButton } from "@serendie/ui";
import { Menu } from "../../Dialogs/Menu/Menu";
import { MenuItem } from "../../Dialogs/Menu/MenuItem";
import { ArrayHelper } from "@iconics/core";

export class AssetWorXPlusTreeView extends BaseComponent<IAssetWorXPlusTreeViewProps, IAssetWorXPlusTreeViewState>
{
    constructor(props: IAssetWorXPlusTreeViewProps)
    {
        super(props);
        this.state = {};
    }

    public async componentDidMount(): Promise<void>
    {
        const nodes = await this.props.onGetNodes();

        const nodesWithChildren = nodes.map(i => ({
            ...i,
            children: undefined,
            expanded: false,
            leaf: false,

        })) satisfies IAssetWorXPlusTreeViewItemWithChildren[];

        await this.props.onNodesChange(nodesWithChildren);
        if (this.props.nodes[0])
        {
            await this.handleNodeClick(this.props.nodes[0]);
        }

        this.setState({ expandedNodeId: this.props.expandedNodeId });
    }

    public async componentDidUpdate(prevProps: IAssetWorXPlusTreeViewProps, prevState: IAssetWorXPlusTreeViewState): Promise<void>
    {
        if (prevProps.expandedNodeId != this.props.expandedNodeId)
        {
            this.setState({ expandedNodeId: this.props.expandedNodeId });
        }
        if (prevState.expandedNodeId != this.state.expandedNodeId)
        {
            this.expandNodes();
        }
    }

    private async expandNodes(): Promise<void>
    {
        if (!this.props.expandedNodeId)
        {
            return;
        }
        let ancestorIds = await this.props.onGetAncestorNodes(this.props.expandedNodeId);
        ancestorIds = ancestorIds.filter(i => i != this.props.expandedNodeId);
        let children = this.props.nodes;

        for (const ancestorId of ancestorIds)
        {
            const nodeToExpand = children.find(i => i.id == ancestorId);
            if (!nodeToExpand)
            {
                return;
            }
            else if (!nodeToExpand.expanded)
            {
                await this.handleNodeClick(nodeToExpand);
            }

            const freshNodeToExpand = ArrayHelper
                .flattenTree(this.props.nodes, i => i.id, i => i.children ?? [])
                .find(i => i.id == ancestorId);

            children = freshNodeToExpand?.children ?? [];
        }
    }

    private async handleNodeClick(node: IAssetWorXPlusTreeViewItemWithChildren): Promise<void>
    {
        if (node.leaf)
        {
            return;
        }
        else if (node.expanded)
        {
            await this.setNodeState(node.id, n => ({ ...n, expanded: false }));
        }
        else if (node.children)
        {
            await this.setNodeState(node.id, n => ({ ...n, expanded: true }));
        }
        else
        {
            let nodesWithChildren: IAssetWorXPlusTreeViewItemWithChildren[];
            try
            {
                const nodes = await this.props.onGetNodes(node.id);

                nodesWithChildren = nodes.map(i => ({
                    ...i,
                    children: undefined,
                    expanded: false,
                    leaf: false,

                })) satisfies IAssetWorXPlusTreeViewItemWithChildren[];
            }
            catch
            {
                nodesWithChildren = [];
            }

            await this.setNodeState(node.id, n => ({
                ...n,
                expanded: true,
                leaf: nodesWithChildren.length == 0,
                children: nodesWithChildren,
            }));
        }
    }

    private async setNodeState(id: number, updateNode: (node: IAssetWorXPlusTreeViewItemWithChildren) => IAssetWorXPlusTreeViewItemWithChildren): Promise<void>
    {
        const copyNodes = (nodes: IAssetWorXPlusTreeViewItemWithChildren[]): IAssetWorXPlusTreeViewItemWithChildren[] =>
        {
            return nodes.map(node =>
            {
                if (node.id === id)
                {
                    return updateNode({ ...node });
                }
                else if (node.children)
                {
                    return { ...node, children: copyNodes(node.children) };
                }
                else
                {
                    return { ...node };
                }
            });
        };

        await this.props.onNodesChange(copyNodes(this.props.nodes));
    }

    private handleMenuShow(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, node: IAssetWorXPlusTreeViewItem): void
    {
        event.stopPropagation();
        this.setState({ selectedNode: node, menuAnchor: event.currentTarget });
    }

    private handleMenuHide(): void
    {
        this.setState({ selectedNode: undefined, menuAnchor: undefined });
    }

    private handleMenuItemClick(menuItem: IAssetWorXPlusTreeViewMenuItem): void
    {
        if (!this.state.selectedNode)
        {
            return;
        }
        menuItem.onClick(this.state.selectedNode);
        this.setState({ selectedNode: undefined, menuAnchor: undefined });
    }

    public render(): JSX.Element
    {
        return (
            <div
                className='asset-worx-plus-tree-view'
            >
                {this.renderNodes(this.props.nodes, 0)}
                <Menu
                    anchor={this.state.menuAnchor ?? null}
                    open={!!this.state.menuAnchor}
                    onClose={() => this.handleMenuHide()}
                    className='asset-worx-plus-tree-view__menu'
                >
                    {
                        this.props.menu.map(i =>
                            <MenuItem
                                key={i.id}
                                onClick={() => this.handleMenuItemClick(i)}
                                className='asset-worx-plus-tree-view__menu-item'
                            >
                                {i.name}
                            </MenuItem>
                        )
                    }
                </Menu>
            </div>
        );
    }

    private renderNodes(nodes: IAssetWorXPlusTreeViewItemWithChildren[], level: number): JSX.Element
    {
        const paddingLeft = 32 * level;

        return (
            <>
                {nodes.map(node =>
                {
                    const expandIcon = (node.expanded ? 'chevron-down' : 'chevron-right');

                    return (
                        <>
                            <div
                                className='asset-worx-plus-tree-view__node'
                                style={{
                                    paddingLeft: `${paddingLeft}px`,
                                }}
                                onClick={() => this.handleNodeClick(node)}
                            >
                                <div
                                    className='asset-worx-plus-tree-view__expand'
                                >
                                    {!node.leaf && <SerendieSymbol name={expandIcon} />}
                                </div>
                                <div
                                    className='asset-worx-plus-tree-view__name'
                                >
                                    {node.name}
                                </div>
                                <div
                                    className='asset-worx-plus-tree-view__menu-toggle'
                                >
                                    <IconButton
                                        size='small'
                                        shape="circle"
                                        styleType="ghost"
                                        icon={<SerendieSymbolMoreVertical />}
                                        onClick={e => this.handleMenuShow(e, node)}
                                        style={{ flexShrink: 0 }}
                                    />
                                </div>
                            </div>
                            {node.expanded && node.children && this.renderNodes(node.children, level + 1)}
                        </>
                    );
                }
                )}
            </>
        );
    }
}

export interface IAssetWorXPlusTreeViewProps
{
    nodes: IAssetWorXPlusTreeViewItemWithChildren[];
    menu: IAssetWorXPlusTreeViewMenuItem[];
    expandedNodeId?: number;
    onNodesChange: (items: IAssetWorXPlusTreeViewItemWithChildren[]) => Promise<void>;
    onGetNodes: (parentId?: number) => Promise<IAssetWorXPlusTreeViewItem[]>;
    onGetAncestorNodes: (nodeId: number) => Promise<number[]>
}

export interface IAssetWorXPlusTreeViewState
{
    selectedNode?: IAssetWorXPlusTreeViewItem;
    menuAnchor?: EventTarget & HTMLButtonElement;
    expandedNodeId?: number;
}

export interface IAssetWorXPlusTreeViewItem
{
    id: number;
    parentId?: number;
    name: string;
}

export interface IAssetWorXPlusTreeViewItemWithChildren extends IAssetWorXPlusTreeViewItem
{
    children?: IAssetWorXPlusTreeViewItemWithChildren[];
    expanded: boolean;
    leaf: boolean;
}

export interface IAssetWorXPlusTreeViewMenuItem
{
    id: string;
    name: string;
    onClick: (node: IAssetWorXPlusTreeViewItem) => void;
}
